'use client';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { Brand, fetchBrand, getLoadedBrand } from './load-brand';
import { captureBrand } from './brand-key';

// undefined = no provider: fall back to the brand loaded by initBrand() (SPAs).
// SSR apps wrap the tree in BrandProvider with the brand resolved per request.
const BrandContext = createContext<Brand | null | undefined>(undefined);

export function BrandProvider({ brand, children }: PropsWithChildren<{ brand: Brand | null }>) {
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

// SSR apps: renders the brand resolved from the request cookie, then applies the
// client-side rules (?brand=, utm_source reset) and refetches only if the key changed.
export function CapturingBrandProvider({
  initialBrand,
  brandApiUrl,
  children,
}: PropsWithChildren<{ initialBrand: Brand | null; brandApiUrl: string }>) {
  const [brand, setBrand] = useState(initialBrand);

  useEffect(() => {
    const key = captureBrand();
    if ((key ?? null) === (initialBrand?.key ?? null)) return;
    if (!key) {
      setBrand(null);
      return;
    }
    fetchBrand(brandApiUrl, key).then(setBrand);
  }, []);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand(): Brand | null {
  const brand = useContext(BrandContext);
  return brand === undefined ? getLoadedBrand() : brand;
}
